


























import { Doughnut } from 'vue-chartjs/legacy';
import Vue from 'vue';
import { ReportSpec, ReportSpecByWeek } from '@/components/ReportControls.vue';
import { ArcElement, CategoryScale, Chart as ChartJS, Legend, registerables, Title, Tooltip } from 'chart.js';
import axios from 'axios';
import SvgIcon from '@jamescoyle/vue-icon';
import { mdiAutorenew } from '@mdi/js';
import { DateTime } from "luxon";

ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale, ...registerables);

interface LateJob {
  name: string;
  id: string;
  og_due: string;
  og_time: string;
  del_completed: string;
  del_time: string;
  status: string;
  bu: number;
}

export default Vue.extend({
  name: 'DoughnutChart',
  components: {
    Doughnut,
    SvgIcon,
  },
  props: {
    chartId: {
      type: String,
      default: 'ontime-chart',
    },
    datasetIdKey: {
      type: String,
      default: 'label',
    },
    plugins: {
      type: Array,
      default: () => [],
    },
    width: {
      type: Number,
      default: 400,
    },
    height: {
      type: Number,
      default: 280,
    },
    spec: Object as () => ReportSpec,
  },
  computed: {
    dataUrl(): string {
      const BASE_URL = (process.env.NODE_ENV === 'production') ? '/api' : '//localhost:8000';
      if (this.spec?.dateStyle == "calendar") {
        const { depot, from, to } = this.spec;
        return `${BASE_URL}/reports/customer-review-jobs-on-time/${depot}/${DateTime.fromJSDate(from).toISODate()}/${DateTime.fromJSDate(to).toISODate()}/`;
      } else {
        const { depot, from, to } = this.spec;
        return `${BASE_URL}/reports/customer-review-jobs-on-time/${depot}/${from.year}/${from.week}/${to.year}/${to.week}/`;
      }
    },
    downloadUrl(): string {
      const BASE_URL = (process.env.NODE_ENV === 'production') ? '/api' : '//localhost:8000';
      if (this.spec?.dateStyle == "calendar") {
        const { depot, from, to } = this.spec;
        return `${BASE_URL}/reports/customer-review-late-download/${depot}/${DateTime.fromJSDate(from).toISODate()}/${DateTime.fromJSDate(to).toISODate()}/`;
      } else {
        const { depot, from, to } = this.spec;
        return `${BASE_URL}/reports/customer-review-late-download/${depot}/${from.year}/${from.week}/${to.year}/${to.week}/`;
      }
    },
  },
  data() {
    return {
      path: mdiAutorenew,
      size: 48,
      loading: true,
      onTime: 0,
      late: 0,
      lateJobs: [] as LateJob[],
      chartData: {
        labels: ['On Time'],
        datasets: [
          {
            type: 'doughnut',
            backgroundColor: ['#7fba50', 'White'],
            data: [] as number[],
          },
        ],
      },
      chartOptions: {
        cutout: '70%',
        resizeDelay: 2,
        responsive: false,
        maintainAspectRatio: false,
        radius: '95%',
        layout: {
          padding: {
            top: 10,
            bottom: 20,
            left: 20,
            right: 20,
          },
        },
        plugins: {
          title: {
            display: true,
            text: 'Consignments On Time',
            padding: {
              top: 1,
              bottom: 1,
            },
            font: {
              size: 16,
            },
          },
          legend: {
            display: true,
            position: 'top',
            align: 'center',
            labels: {
              padding: 10,
              boxWidth: 12,
              font: {
                size: 14,
              },
            },
          },
          datalabels: {
            display: false,
          },
          tooltip: {
            backgroundColor: '#0B234E',
            borderColor: 'rgba(130, 189, 83, 80)',
            borderWidth: 1,
            caretPadding: 10,
            displayColors: false,
            xAlign: 'center',
            yAlign: 'center',
            position: 'nearest',
            titleAlign: 'center',
            titleMarginBottom: 0,
            titleSpacing: 0,
            footerAlign: 'right',
            footerSpacing: 2,
            bodySpacing: 0,
            padding: {
              top: 8,
              bottom: 8,
              left: 6,
              right: 10,
            },
            titleFont: {
              size: 12,
            },
            footerFont: {
              size: 10,
            },
            labelAlign: 'left',
            callbacks: {
              title: (context: any) => {
                const titleText: string = context[0].dataIndex === 0
                  ? `On Time: ${context[0].formattedValue}`
                  : `Late: ${context[0].formattedValue}`;
                return titleText;
              },
              label: () => '', // Empty to allow for title + footer combo without default label getting inserted
              footer: (context: any) => {
                // The typecasts in this are ugly, but I think they're correct, and it seems to work. The problem
                // seems to be that TS is still working out the type of the component at this point, so doesn't know
                // that this.lateJobs does in fact exist
                const custInfo:
                    Array<{name: string, bu: number, og_due: string, og_time: string, del_completed: string,
                      del_time: string, status: string}> = (this as unknown as {lateJobs: LateJob[]}).lateJobs;
                const custList = custInfo.map((item) => {
                  if (item.del_completed == null) {
                    const ogDueSplit = item.og_due.split('-');
                    const ogTimeSplit = item.og_time.split(':');
                    const trimmedName = item.name.substring(0, 9) + '\t';
                    return {...item,
                      name: trimmedName,
                      og_due: `${ogDueSplit[2]}/${ogDueSplit[1]}`,
                      og_time: `${ogTimeSplit[0]}:${ogTimeSplit[1]}`,
                      del_completed: 'N/A',
                      del_time: 'N/A',
                      status: item.status,
                    };
                  } else {
                    const ogDueSplit = item.og_due.split('-');
                    const ogTimeSplit = item.og_time.split(':');
                    const delCompSplit = item.del_completed.split('-');
                    const delTimeSplit = item.del_time.split(':');
                    const trimmedName = item.name.substring(0, 9) + '\t';
                    return { ...item,
                      name: trimmedName,
                      og_due: `${ogDueSplit[2]}/${ogDueSplit[1]}`,
                      og_time: `${ogTimeSplit[0]}:${ogTimeSplit[1]}`,
                      del_completed: `${delCompSplit[2]}/${delCompSplit[1]}`,
                      del_time: `${delTimeSplit[0]}:${delTimeSplit[1]}`,
                      status:
                          `DEL ${delCompSplit[2]}/${delCompSplit[1]} @ ${delTimeSplit[0]}:${delTimeSplit[1]}`,
                    };
                  }
                });
                custList.sort((item1: any, item2: any) => item2.bu - item1.bu);
                const formattedList: string[] = [];
                custList.forEach((item: any) => {
                  formattedList.push(
                      `${item.name} (+${item.bu})\t |\t Due: ${item.og_due} @ ${item.og_time}\t |\t Status: ${item.status}`,
                  );
                });
                return context[0].dataIndex === 0
                    ? ''
                    : formattedList.slice(0, 15);
              },
            },
          },
        },
      },
    };
  },
  watch: {
    spec: {
      immediate: true,
      async handler() {
        const response = await axios.get<{ Late: number, OnTime: number, LateJobs: [] }>(this.dataUrl, {
          params: {
            account_codes: this.spec.accountCodes.flatMap((a) => a.codes),
          },
        });
        this.onTime = response.data.OnTime;
        this.late = response.data.Late;
        this.lateJobs = response.data.LateJobs;
        this.chartData.datasets[0].data = [this.onTime, this.late];
        this.loading = false;
      },
    },
  },
  methods: {
    async downloadRequest() {
      try {
        await axios.get(this.downloadUrl, {
        params: {
          account_codes: this.spec.accountCodes.flatMap((a) => a.codes),
        },
        responseType: 'blob',
        }).then((response) => {
          const a = document.createElement('a');
          a.href = window.URL.createObjectURL(response.data);
          a.download = `hm_late_data_${new Date(Date.now()).toLocaleDateString()}.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        });
      } catch (error) {
        alert('No data available. Please check the filters and try again.');
      }
    },
  },
});
